import { useGetBotPositionsHistory } from "@/api/positions/positions-history-bot";
import { useWallet } from "@/providers/wallet";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { IconComponent } from "@/ui/icon";
import LoadingIndicator from "@/ui/loading-indicator";
import Table from "@/ui/table";
import PortfolioTWChart from "./portfolio-tw-chart";
import { Amount, Price } from "@/ui/formats";
import { useState } from "react";
import clsx from "clsx";

export type ChartPnlType = "Cumulative PNL" | "Daily PNL";

const pnls = ["Cumulative PNL", "Daily PNL"];

const PortfolioChart = () => {
  const { userId, currentWallet } = useWallet();

  const { data, isLoading, isError } = useGetBotPositionsHistory(
    userId,
    currentWallet
  );

  const [activePnl, setActivePnl] = useState<ChartPnlType>(
    pnls[0] as ChartPnlType
  );

  const renderTableRowItem = (value?: number, valueUsd?: number) => {
    if (value === undefined || valueUsd === undefined) {
      return <span>-</span>;
    }

    return (
      <div className="flex items-center gap-x-[0.3125rem]">
        <IconComponent
          name={currentWallet.walletType}
          className="w-[0.9375rem] h-[0.9375rem] shrink-0"
        />
        <span className="flex items-center gap-x-[0.2125rem]">
          <Amount value={value} />
          <div className="flex">
            (
            <Price value={valueUsd} />)
          </div>
        </span>
      </div>
    );
  };

  return (
    <GradientBorderRadius overflow className="h-full flex flex-col">
      <div className="relative shrink-0 w-full h-[1.375rem] flex justify-between items-center my-[0.625rem] px-[0.625rem]">
        {currentWallet && data && data.historicalData.length > 0 && (
          <div className="flex items-center gap-x-[0.625rem] px-[0.3125rem]">
            {pnls.map((pnl) => {
              const isActive = activePnl === pnl;

              return (
                <span
                  key={pnl}
                  onClick={() => setActivePnl(pnl as ChartPnlType)}
                  className={clsx(
                    "cursor-pointer text-white font-semibold text-xs hover:text-opacity-100",
                    isActive ? "text-opacity-100" : "text-opacity-50"
                  )}
                >
                  {pnl}
                </span>
              );
            })}
          </div>
        )}
        <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
          <div className="text-base leading-[0.625rem] font-semibold">
            PNL Chart
          </div>
        </div>
      </div>
      <Table className="text-left text-xs shrink-0">
        <colgroup>
          <col style={{ width: "50%" }} />
          <col style={{ width: "50%" }} />
          <col className="w-full" />
        </colgroup>
        <Table.Header className="h-[1.5625rem] bg-black">
          <Table.HeaderColumn>Balance</Table.HeaderColumn>
          <Table.HeaderColumn>Positions</Table.HeaderColumn>
          <Table.HeaderColumn className="w-full" />
        </Table.Header>
        <Table.Body>
          {!currentWallet ? (
            <Table.Row>
              <Table.Column colSpan={3} className="!p-[0.625rem]">
                Sign in to view data
              </Table.Column>
            </Table.Row>
          ) : isLoading ? (
            <Table.Row>
              <Table.Column className="!p-0" colSpan={3}>
                <LoadingIndicator />
              </Table.Column>
            </Table.Row>
          ) : isError ? (
            <Table.Row>
              <Table.Column colSpan={3} className="!p-[0.625rem]">
                Something went wrong while loading your wallet transactions.
                Please try again later.
              </Table.Column>
            </Table.Row>
          ) : (
            <Table.Row className="h-[1.875rem] border-none">
              <Table.Column>
                {renderTableRowItem(Number(data?.balance), data?.balanceUsd)}
              </Table.Column>
              <Table.Column>
                {renderTableRowItem(data?.positions, data?.positionsValue)}
              </Table.Column>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {currentWallet && data && data.historicalData.length > 0 && (
        <PortfolioTWChart data={data.historicalData} activePnl={activePnl} />
      )}
    </GradientBorderRadius>
  );
};

export default PortfolioChart;
